import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import axios from 'axios';

const formatIssuedDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

const getRecipientLabel = (issuance) => {
  if (issuance?.issuedToExternalName) return issuance.issuedToExternalName;
  const user = issuance?.issuedTo;
  if (user && (user.firstName || user.lastName)) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  return issuance?.issuedToExternalDepartment || '—';
};

const aggregateParts = (issuances) => {
  const byPart = new Map();
  for (const issuance of issuances) {
    for (const item of issuance.items || []) {
      const partNumber = String(item.partNumber || '').trim() || '—';
      const description =
        item.inventoryItemId?.description ||
        item.description ||
        '';
      const issued = Number(item.quantity) || 0;
      const returned = Number(item.quantityReturned) || 0;
      const current = byPart.get(partNumber) || {
        partNumber,
        description: '',
        issued: 0,
        returned: 0,
        net: 0
      };
      if (!current.description && description) current.description = description;
      current.issued += issued;
      current.returned += returned;
      current.net += issued - returned;
      byPart.set(partNumber, current);
    }
  }
  return [...byPart.values()].sort((a, b) => a.partNumber.localeCompare(b.partNumber));
};

/**
 * Read-only store issuance summary for a work order (MRO workbench / maintenance detail).
 */
const WorkOrderIssuedPartsPanel = ({ workOrderId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [issuances, setIssuances] = useState([]);

  const fetchIssuances = useCallback(async () => {
    if (!workOrderId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/store/issuances', {
        params: {
          workOrderId,
          status: 'ISSUED,PARTIAL_RETURN,RETURNED'
        }
      });
      setIssuances(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load issued parts from store');
      setIssuances([]);
    } finally {
      setLoading(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    fetchIssuances();
  }, [fetchIssuances]);

  const aggregated = useMemo(() => aggregateParts(issuances), [issuances]);
  const issuanceBatches = useMemo(
    () => [...issuances].sort((a, b) => new Date(b.issuedDate) - new Date(a.issuedDate)),
    [issuances]
  );

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Parts issued from store
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">Loading issued parts...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : aggregated.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No parts have been issued from store for this work order yet.
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Part number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Issued qty</TableCell>
                  <TableCell align="right">Returned qty</TableCell>
                  <TableCell align="right">Net with MRO</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aggregated.map((row) => (
                  <TableRow key={row.partNumber}>
                    <TableCell>{row.partNumber}</TableCell>
                    <TableCell>{row.description || '—'}</TableCell>
                    <TableCell align="right">{row.issued}</TableCell>
                    <TableCell align="right">{row.returned}</TableCell>
                    <TableCell align="right">{row.net}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {issuanceBatches.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Issuance history ({issuanceBatches.length} record{issuanceBatches.length === 1 ? '' : 's'})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Issued to</TableCell>
                      <TableCell>Challan</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {issuanceBatches.map((issuance) => (
                      <TableRow key={issuance._id}>
                        <TableCell>{formatIssuedDate(issuance.issuedDate)}</TableCell>
                        <TableCell>{getRecipientLabel(issuance)}</TableCell>
                        <TableCell>{issuance.challanNumber || '—'}</TableCell>
                        <TableCell>{issuance.status || '—'}</TableCell>
                        <TableCell>
                          {(issuance.items || [])
                            .map((item) => {
                              const qty = Number(item.quantity) || 0;
                              const returned = Number(item.quantityReturned) || 0;
                              const net = qty - returned;
                              return `${item.partNumber || '—'} × ${net}${returned > 0 ? ` (${qty} issued, ${returned} returned)` : ''}`;
                            })
                            .join('; ') || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default WorkOrderIssuedPartsPanel;
